/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate } from "react-router";
import {
  ControlledInput,
  ControlledSelect,
  DynamicFormInput,
  FormUpload,
  PrimaryButton,
  SecondaryButton,
} from "../../../components";
import { withProtectedRoute } from "../../../utils/HOC/withProtectedRoute";
import { useForm } from "react-hook-form";
import { useEffect, useMemo, useState } from "react";
import { useMarketPricing } from "../../../store/useMarketPricing";
import { useStatiegeldProducts } from "../../../store/useStatiegeldProducts";
import {
  REPEATABLE_CHALLENGE_TYPES,
  usePartnerChallenges,
} from "../../../store/usePartnerChallenges";
import { usePartners } from "../../../store/usePartners";
import { useUploadImage } from "../../../store/useUploadImage";
import {
  BANNER_IMAGE_HINT,
  CARD_IMAGE_HINT,
  ImageUploadHint,
} from "../../../components/form/ImageUploadHint";
import { useDropLocations } from "../../../store/useDropLocations";
import { storeLabel } from "../../../utils/storeLabel";
import { useVouchers } from "../../../store/useVouchers";
import { useAlert } from "../../../store/useAlert";
import { Switch } from "@material-tailwind/react";

type Inputs = {
  en_title: string;
  nl_title: string;
  en_description: string;
  nl_description: string;
  partnerId: string;
  startsAt?: string;
  endsAt?: string;
  rewardType: "VOUCHER" | "CASH" | "DROPPIES";
  rewardValue?: number;
  type:
    | "FIRST_DROP"
    | "DROP_COUNT"
    | "DROP_VALUE"
    | "ITEM_TYPE"
    | "ITEM_TYPE_COUNT"
    | "ITEM_TYPE_DROP_COUNT"
    | "ITEM_TYPE_VALUE"
    | "TOMRA_ITEM_TYPE"
    | "TOMRA_ITEM_TYPE_COUNT"
    | "TOMRA_ITEM_TYPE_DROP_COUNT"
    | "TOMRA_ITEM_TYPE_VALUE"
    | "DONATION"
    | "INVITE"
    | "STREAKS"
    | "POSTAL_CODE"
    | "COMPLETE_PROFILE"
    | "PARCEL_COUNT";
  minDrops?: number;
  minValue?: number;
  itemType?: string;
  tomraItemType?: string;
  streakType?: "DAILY" | "WEEKLY" | "MONTHLY";
  streakCount?: number;
  donationCount?: number;
  minDonationAmount?: number;
  parcelDirection?: "PICKUP" | "DROPOFF" | "EITHER";
  minParcelCount?: number;
};

function AddPartnerChallengeScreen() {
  /** Hooks */
  const navigate = useNavigate();
  const {
    createPartnerChallenge,
    createdPartnerChallenge,
    clearCreatedPartnerChallenge,
    recurringChallengeTypes,
    getChallengeCapabilities,
  } = usePartnerChallenges();
  const { getAllPartners, allPartners, getCouriers, couriers } = usePartners();
  const { setAlert } = useAlert();
  const { getAllMarketPrices, marketPrices } = useMarketPricing();
  const { getStatiegeldProducts, statiegeldProducts } = useStatiegeldProducts();
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    reset,
  } = useForm<Inputs>();
  const { uploadImage, clearUploadedImage, uploadedImage, uploadFile } =
    useUploadImage();
  const { dropLocations, getDropLocations } = useDropLocations();
  const {
    activeVouchers,
    getPartnerActiveVouchers,
    clearPartnerActiveVouchers,
  } = useVouchers();
  /** State */
  const [image, setImage] = useState<File | null>(null);
  const [cardImage, setCardImage] = useState<File | null>(null);
  const [cardImageUrl, setCardImageUrl] = useState<string | null>(null);
  const [cardUploading, setCardUploading] = useState<boolean>(false);
  const [cities, setCities] = useState<
    {
      id: number | string;
      value: string;
    }[]
  >([]);
  // Where the challenge runs: every store in the chosen cities, or only the
  // chosen stores. The backend ignores cities when stores are set.
  const [targetType, setTargetType] = useState<"cities" | "locations">(
    "cities"
  );
  const [stores, setStores] = useState<
    {
      id: number | string;
      value: string;
    }[]
  >([]);
  // Repeat completions. Kept out of react-hook-form because it only applies
  // to some condition types and is cleared whenever the type moves away.
  const [allowMultipleCompletions, setAllowMultipleCompletions] =
    useState<boolean>(false);
  const [carriers, setCarriers] = useState<
    {
      id: number | string;
      value: string;
    }[]
  >([]);

  /** Callbacks */
  const goBack = () => navigate(-1);
  const onSubmit = (data: Inputs) => {
    if (cardUploading) {
      setAlert({
        isOpen: true,
        severity: "error",
        message: "Wait for the card image to finish uploading.",
      });
      return;
    }
    if (targetType === "locations" && stores.length === 0) {
      setAlert({
        isOpen: true,
        severity: "error",
        message: "Pick at least one store, or switch to Cities.",
      });
      return;
    }
    const body: any = {
      en_title: data.en_title,
      nl_title: data.nl_title,
      en_description: data.en_description,
      nl_description: data.nl_description,
      conditions: {
        type: data.type,
      },
      rewardType: data.type === "PARCEL_COUNT" ? "VOUCHER" : data.rewardType,
      partnerId: data?.partnerId,
    };
    // Never on a parcel challenge: the field is hidden, and sending a stale
    // value the form still holds is how a rejected save happens.
    if (
      data.type !== "PARCEL_COUNT" &&
      data.rewardType !== "VOUCHER" &&
      data.rewardValue
    ) {
      body.rewardValue = data.rewardValue;
    }
    if (uploadedImage) {
      body.backgroundImage = uploadedImage;
    }
    if (cardImageUrl) {
      body.cardImage = cardImageUrl;
    }
    if (data.type === "ITEM_TYPE") {
      body.conditions.itemType = data.itemType;
    }
    if (
      data.type === "ITEM_TYPE_DROP_COUNT" ||
      data.type === "ITEM_TYPE_COUNT"
    ) {
      body.conditions.itemType = data.itemType;
      if (data.minDrops) {
        body.conditions.minDrops = data.minDrops;
      }
    }
    if (data.type === "ITEM_TYPE_VALUE") {
      body.conditions.itemType = data.itemType;
      if (data.minValue) {
        body.conditions.minValue = Number(data.minValue * 100) as number;
      }
    }
    if (data.type === "DROP_COUNT") {
      if (data.minDrops) {
        body.conditions.minDrops = data.minDrops;
      }
    }
    if (data.type === "DROP_VALUE") {
      if (data.minValue) {
        body.conditions.minValue = Number(data.minValue * 100) as number;
      }
    }
    if (data?.type === "DONATION") {
      body.conditions.donationCount = data.donationCount;
      if (data.minDonationAmount) {
        body.conditions.minDonationAmount = Number(
          data.minDonationAmount * 100
        ) as number;
      }
    }
    if (data.type === "TOMRA_ITEM_TYPE") {
      body.conditions.tomraItemType = data.tomraItemType;
    }
    if (
      data.type === "TOMRA_ITEM_TYPE_DROP_COUNT" ||
      data.type === "TOMRA_ITEM_TYPE_COUNT"
    ) {
      body.conditions.tomraItemType = data.tomraItemType;
      if (data.minDrops) {
        body.conditions.minDrops = data.minDrops;
      }
    }
    if (data.type === "TOMRA_ITEM_TYPE_VALUE") {
      body.conditions.tomraItemType = data.tomraItemType;
      if (data.minValue) {
        body.conditions.minValue = Number(data.minValue * 100) as number;
      }
    }
    if (data.type === "STREAKS") {
      body.conditions.streakType = data.streakType;
      body.conditions.streakCount = data.streakCount;
    }
    if (data.type === "PARCEL_COUNT") {
      // The backend rejects this too; stopping here keeps the form filled in
      // instead of bouncing the marketer off a failed request. Says so out
      // loud rather than returning silently, which reads as a dead button.
      if (carriers.length === 0) {
        setAlert({
          isOpen: true,
          severity: "error",
          message: "Pick at least one carrier for a parcel challenge.",
        });
        return;
      }
      body.conditions.carrierPartnerIds = carriers.map((c) => String(c.id));
      body.conditions.parcelDirection = data.parcelDirection ?? "EITHER";
      body.conditions.minParcelCount = Number(data.minParcelCount);
    }
    if (REPEATABLE_CHALLENGE_TYPES.includes(data.type)) {
      body.allowMultipleCompletions = allowMultipleCompletions;
    }
    if (targetType === "locations") {
      body.locationIds = stores.map((input) => String(input?.id));
    } else if (cities?.length) {
      body.cities = cities?.map((input) => input?.id) ?? [];
    }
    // Left out of the body when empty so the challenge stays "live now"
    // rather than being pinned to a date the marketer never chose.
    if (data.startsAt) {
      body.startsAt = data.startsAt;
    }
    if (data.endsAt) {
      body.endsAt = data.endsAt;
    }
    createPartnerChallenge(body);
  };

  const onUploadImage = () => {
    if (!image) return;
    uploadImage(image);
  };

  const onImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files) {
      setImage(e.target.files[0]);
    }
  };

  const onCardImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e?.target?.files?.[0];
    // Cleared so that picking the same file again still fires a change.
    e.target.value = "";
    if (file) {
      setCardImage(file);
    }
  };

  /** Effects */
  useEffect(() => {
    getAllMarketPrices();
    getStatiegeldProducts();
    getAllPartners(1, 1000);
    getCouriers();
    getDropLocations();
    getChallengeCapabilities();
    reset({ rewardType: "VOUCHER" });
    return () => {
      clearUploadedImage();
      clearPartnerActiveVouchers();
    };
  }, []);

  useEffect(() => {
    if (createdPartnerChallenge) {
      navigate(-1);
      clearUploadedImage();
      clearCreatedPartnerChallenge();
    }
  }, [createdPartnerChallenge]);

  useEffect(() => {
    if (!cardImage) return;
    let current = true;
    setCardUploading(true);
    uploadFile(cardImage).then((url) => {
      if (!current) return;
      setCardUploading(false);
      if (url) {
        setCardImageUrl(url);
        return;
      }
      // Keep the preview in step with what Save sends: a failed upload
      // leaves the previous card image (or none) in place.
      setCardImage(null);
      setAlert({
        isOpen: true,
        severity: "error",
        message: "The card image could not be uploaded. Please try again.",
      });
    });
    return () => {
      current = false;
      setCardUploading(false);
    };
  }, [cardImage]);

  useEffect(() => {
    if (image) {
      onUploadImage();
    }
  }, [image]);

  useEffect(() => {
    if (watch("partnerId")) {
      getPartnerActiveVouchers(watch("partnerId"));
    }
  }, [watch("partnerId")]);

  const selectedPartner = useMemo(
    () => allPartners?.find((p) => p?._id === watch("partnerId")) ?? null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allPartners, watch("partnerId")]
  );

  // A courier partner only ever backs a parcel challenge, so choosing one
  // settles the type - the same way a parcel type settles the reward below.
  const isCourierPartner = Boolean(selectedPartner?.isCourier);
  useEffect(() => {
    if (isCourierPartner && watch("type") !== "PARCEL_COUNT") {
      setValue("type", "PARCEL_COUNT");
    }
  }, [isCourierPartner]);

  // A parcel challenge can only pay a voucher — parcels move no money
  // anywhere else, and the backend refuses anything else outright. Forcing it
  // here means the operator never fills in a form the server will reject.
  const isParcelChallenge = watch("type") === "PARCEL_COUNT";
  useEffect(() => {
    if (isParcelChallenge && watch("rewardType") !== "VOUCHER") {
      setValue("rewardType", "VOUCHER");
    }
  }, [isParcelChallenge]);

  // Repeat completions exist only for the types that can repeat, and only
  // where this environment allows that type; the backend rejects the flag
  // anywhere else, so it must not survive a type change. Allowed until the
  // capabilities arrive, so the switch does not flash disabled.
  const canRepeat = REPEATABLE_CHALLENGE_TYPES.includes(watch("type"));
  const isRepeatAllowed =
    recurringChallengeTypes === null ||
    recurringChallengeTypes.includes(watch("type"));
  useEffect(() => {
    if ((!canRepeat || !isRepeatAllowed) && allowMultipleCompletions) {
      setAllowMultipleCompletions(false);
    }
  }, [canRepeat, isRepeatAllowed]);

  // What one completion takes, for the helper under the switch.
  const repeatCycleLabel = () => {
    const minDrops = watch("minDrops") || "N";
    switch (watch("type")) {
      case "DROP_COUNT":
        return `${minDrops} drops`;
      case "ITEM_TYPE_COUNT":
      case "ITEM_TYPE_DROP_COUNT":
      case "TOMRA_ITEM_TYPE_DROP_COUNT":
        return `${minDrops} qualifying drops`;
      case "TOMRA_ITEM_TYPE_COUNT":
        return `${minDrops} matching items`;
      case "DROP_VALUE":
      case "ITEM_TYPE_VALUE":
      case "TOMRA_ITEM_TYPE_VALUE":
        return watch("minValue")
          ? `${formatEuros(Number(watch("minValue")))} of drop value`
          : "€N of drop value";
      case "STREAKS": {
        const period = streakPeriods[watch("streakType") ?? ""] ?? "periods";
        return `${watch("streakCount") || "N"} consecutive ${period}`;
      }
      case "DONATION":
        return `${watch("donationCount") || "N"} qualifying donations`;
      default:
        return "";
    }
  };

  // Every completion pays out again, which is what an admin setting a budget
  // has to know before switching this on. Parcel keeps its own copy below.
  const repeatRewardText = () => {
    if (!allowMultipleCompletions) {
      return "Each customer can complete this challenge once.";
    }
    if (watch("rewardType") === "VOUCHER") {
      return "Each completion issues its own voucher, so stock is consumed as fast as customers earn it.";
    }
    const reward = formatEuros(Number(watch("rewardValue") || 0));
    return `Each completion pays the reward again, so a customer earns ${reward} every time they complete it.`;
  };

  const carrierOptions = useMemo(
    () =>
      (couriers ?? []).map((c) => ({
        value: c?._id as string,
        label: c?.name as string,
      })),
    [couriers]
  );

  const citiesToSelect = useMemo(() => {
    if (!dropLocations?.length) return [];
    let locationsToSelect: { value: string; label: string }[] = [];
    for (let location of dropLocations) {
      if (locationsToSelect?.some((loc) => loc?.value === location?.city))
        continue;
      locationsToSelect.push({
        label: location?.city as string,
        value: location?.city as string,
      });
    }
    return locationsToSelect;
  }, [dropLocations]);

  const storesToSelect = useMemo(() => {
    const chosen = stores.map((store) => String(store.id));
    return (dropLocations ?? [])
      .filter((location) => !chosen.includes(String(location?._id)))
      .map((location) => ({
        label: storeLabel(location),
        value: location?._id as string,
      }));
  }, [dropLocations, stores]);

  return (
    <div>
      <div className="flex flex-row items-center mb-6">
        <h1 className="font-satoshi text-2xl font-bold text-dark">
          Add new Partner Challenge
        </h1>
      </div>
      <div className="flex flex-col sm:flex-row">
        <div className="w-full sm:w-1/2 bg-white  border-gray-border drop-shadow-none rounded-xl mb-1 sm:mb-0">
          <div className="w-full border-b border-gray-border px-[28px] py-[15px]">
            <p className="font-satoshi font-medium text-base text-dark">
              New Partner Challenge
            </p>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="w-full px-7 py-7">
            {/* Name */}
            <ControlledInput
              name="en_title"
              rules={{ required: true }}
              control={control}
              error={Boolean(errors?.en_title)}
              label="Title (English)"
              type="text"
              placeholder="Enter title here in English"
            />
            <ControlledInput
              name="nl_title"
              rules={{ required: true }}
              control={control}
              error={Boolean(errors?.nl_title)}
              label="Title (Dutch)"
              type="text"
              placeholder="Enter title here in Dutch"
            />
            {/* Description */}
            <ControlledInput
              name="en_description"
              rules={{ required: true }}
              control={control}
              error={Boolean(errors?.en_description)}
              label="Description (English)"
              type="text"
              placeholder="Enter description here in English"
            />
            <ControlledInput
              name="nl_description"
              rules={{ required: true }}
              control={control}
              error={Boolean(errors?.nl_description)}
              label="Description (Dutch)"
              type="text"
              placeholder="Enter description here in Dutch"
            />
            <ControlledSelect
              name="partnerId"
              control={control}
              rules={{ required: true }}
              error={Boolean(errors?.partnerId)}
              label="Partner"
              options={allPartners?.map((partner) => ({
                _id: partner?._id as string,
                title: partner?.name as string,
              }))}
              placeholder="Pick Partner here"
              inputClassName="w-full"
              className="w-full"
            />
            {/* Never for a carrier: a carrier is out of the app's partner
                list by rule, not by mistake. */}
            {selectedPartner &&
              selectedPartner.showInApp === false &&
              !selectedPartner.isCourier && (
                <p className="font-satoshi text-sm text-red-600 mb-4">
                  This partner is not visible in the app (Show in App is
                  disabled).
                </p>
              )}
            {typeof activeVouchers === "number" &&
            watch("rewardType") === "VOUCHER" ? (
              <div className="mb-6">
                <p
                  className={`font-satoshi text-sm ${
                    activeVouchers === 0 ? "text-red-600" : "text-dark"
                  }`}
                >
                  {activeVouchers} active vouchers from this Partner
                </p>
                {activeVouchers === 0 ? (
                  <div className="mt-2 rounded border border-red-300 bg-red-50 px-3 py-2">
                    <p className="font-satoshi text-sm font-medium text-red-700">
                      No voucher assigned
                    </p>
                    <p className="font-satoshi text-sm text-red-700">
                      A voucher challenge stays hidden in the app until this
                      partner has a voucher. You can save it now and add the
                      voucher under Challenges, Vouchers.
                    </p>
                  </div>
                ) : null}
              </div>
            ) : null}
            <ControlledSelect
              name="rewardType"
              control={control}
              rules={{ required: true }}
              disabled={isParcelChallenge}
              error={Boolean(errors?.rewardType)}
              label="Reward Type"
              options={[
                { _id: "VOUCHER", title: "Voucher" },
                { _id: "CASH", title: "Cash" },
              ]}
              placeholder="Pick reward type here"
            />
            {isParcelChallenge ? (
              <p className="font-satoshi text-sm text-dark -mt-2 mb-4">
                Parcel challenges always reward a voucher.
              </p>
            ) : null}
            {!isParcelChallenge &&
              watch("rewardType") === "CASH" && (
                <>
                  <ControlledInput
                    name="rewardValue"
                    rules={{ required: true }}
                    control={control}
                    error={Boolean(errors?.rewardValue)}
                    label="Reward Value"
                    type="number"
                    placeholder="Enter reward value here"
                  />
                  {watch("rewardType") === "CASH" ? (
                    <p
                      className={`font-satoshi text-sm  mb-6 ${
                        watch("rewardValue") === 0
                          ? "text-red-600"
                          : "text-dark"
                      }`}
                    >
                      {new Intl.NumberFormat("nl-NL", {
                        style: "currency",
                        currency: "EUR",
                      }).format(Number(watch("rewardValue") ?? 0))}{" "}
                      will be added to the User after completing the Challenge
                    </p>
                  ) : null}
                </>
              )}
            {/* Background image upload */}
            <FormUpload
              label="Challenge background image (banner)"
              accept=".png, .jpg, .jpeg"
              formatsHint="PNG or JPG"
              sizeHint="Best: 1200 x 500 px (wide)"
              onChange={onImageChange}
              value={image}
              error={false}
            />
            <ImageUploadHint rows={BANNER_IMAGE_HINT} />
            {/* Card image upload */}
            <FormUpload
              label="Card image (square)"
              accept=".png, .jpg, .jpeg"
              formatsHint="PNG or JPG"
              sizeHint="Best: 800 x 800 px (square)"
              onChange={onCardImageChange}
              value={cardImage}
              error={false}
              onClear={() => {
                setCardImage(null);
                setCardImageUrl(null);
              }}
            />
            <ImageUploadHint rows={CARD_IMAGE_HINT} />
            {/* Challenge Type */}
            <ControlledSelect
              name="type"
              control={control}
              rules={{ required: true }}
              disabled={isCourierPartner}
              error={Boolean(errors?.type)}
              label="Challenge type"
              options={types}
              placeholder="Pick challenge type here"
            />
            {isCourierPartner ? (
              <p className="font-satoshi text-sm text-dark -mt-2 mb-4">
                A courier partner can only be used for a parcel challenge.
              </p>
            ) : null}
            {/* Min Items */}
            {watch("type") === "ITEM_TYPE_COUNT" ||
            watch("type") === "TOMRA_ITEM_TYPE_COUNT" ? (
              <ControlledInput
                name="minDrops"
                rules={{ required: true }}
                control={control}
                error={Boolean(errors?.minDrops)}
                label="Min items"
                type="number"
                placeholder="Enter min items here"
              />
            ) : null}
            {/* Min Drops */}
            {watch("type") === "DROP_COUNT" ||
            watch("type") === "ITEM_TYPE_DROP_COUNT" ||
            watch("type") === "TOMRA_ITEM_TYPE_DROP_COUNT" ? (
              <ControlledInput
                name="minDrops"
                rules={{ required: true }}
                control={control}
                error={Boolean(errors?.minDrops)}
                label="Min Drops"
                type="number"
                placeholder="Enter min drops here"
              />
            ) : null}
            {/* Min value */}
            {watch("type") === "DROP_VALUE" ||
            watch("type") === "ITEM_TYPE_VALUE" ||
            watch("type") === "TOMRA_ITEM_TYPE_VALUE" ? (
              <ControlledInput
                name="minValue"
                rules={{ required: true }}
                control={control}
                error={Boolean(errors?.minValue)}
                label="Min Value"
                type="number"
                placeholder="Enter min value of drop here"
              />
            ) : null}
            {watch("type") === "ITEM_TYPE" ||
            watch("type") === "ITEM_TYPE_COUNT" ||
            watch("type") === "ITEM_TYPE_VALUE" ? (
              <ControlledSelect
                name="itemType"
                control={control}
                rules={{ required: true }}
                error={Boolean(errors?.itemType)}
                label="Composition type"
                options={marketPrices?.map((price) => ({
                  _id: price?._id as string,
                  title: price?.name as string,
                }))}
                placeholder="Pick item type here"
              />
            ) : null}
            {watch("type") === "TOMRA_ITEM_TYPE" ||
            watch("type") === "TOMRA_ITEM_TYPE_COUNT" ||
            watch("type") === "TOMRA_ITEM_TYPE_VALUE" ? (
              <ControlledSelect
                name="tomraItemType"
                control={control}
                rules={{ required: true }}
                error={Boolean(errors?.tomraItemType)}
                label="Statiegeld product type"
                options={statiegeldProducts?.map((price) => ({
                  _id: price?._id as string,
                  title: price?.name as string,
                }))}
                placeholder="Pick Statiegeld item type here"
              />
            ) : null}
            {watch("type") === "STREAKS" ? (
              <ControlledSelect
                name="streakType"
                control={control}
                rules={{ required: true }}
                error={Boolean(errors?.streakType)}
                label="Streak type"
                options={streakTypes?.map((price) => ({
                  _id: price?._id as string,
                  title: price?.title as string,
                }))}
                placeholder="Pick Streak type here"
              />
            ) : null}
            {watch("type") === "STREAKS" ? (
              <ControlledInput
                name="streakCount"
                rules={{ required: true }}
                control={control}
                error={Boolean(errors?.streakCount)}
                label="Streak count"
                type="number"
                placeholder="Enter streak count here"
              />
            ) : null}
            {watch("type") === "DONATION" ? (
              <ControlledInput
                name="donationCount"
                rules={{ required: false }}
                control={control}
                error={Boolean(errors?.donationCount)}
                label="Donation count"
                type="number"
                placeholder="Enter donation count here"
              />
            ) : null}
            {watch("type") === "DONATION" ? (
              <ControlledInput
                name="minDonationAmount"
                rules={{
                  required: allowMultipleCompletions,
                  min: allowMultipleCompletions ? 1 : 0,
                }}
                control={control}
                error={Boolean(errors?.minDonationAmount)}
                label={
                  allowMultipleCompletions
                    ? "Min donation amount in € (at least €1 for repeated completions)"
                    : "Min donation amount in € (optional - leave empty to count any amount)"
                }
                type="number"
                placeholder="Enter min donation amount here"
              />
            ) : null}
            {watch("type") === "PARCEL_COUNT" ? (
              <>
                <DynamicFormInput
                  generateIds={false}
                  addOnSelect
                  label="Carriers (a handoff with any one of these counts)"
                  inputs={carriers}
                  setInputs={setCarriers}
                  options={carrierOptions}
                />
                {carriers.length === 0 ? (
                  <p className="font-satoshi text-sm text-red-600 mb-4">
                    Pick at least one carrier — without one the challenge can
                    never make progress.
                  </p>
                ) : null}
                <ControlledSelect
                  name="parcelDirection"
                  control={control}
                  rules={{ required: true }}
                  error={Boolean(errors?.parcelDirection)}
                  label="Parcel direction"
                  options={parcelDirections}
                  placeholder="Pick which parcels count"
                />
                <ControlledInput
                  name="minParcelCount"
                  rules={{ required: true, min: 1 }}
                  control={control}
                  error={Boolean(errors?.minParcelCount)}
                  label="Minimum parcel count"
                  type="number"
                  placeholder="Enter the number of parcels to reach"
                />
              </>
            ) : null}
            {canRepeat ? (
              <>
                <div className="mb-4">
                  <Switch
                    crossOrigin={"anonymous"}
                    color="teal"
                    ripple
                    label="Allow repeated completions"
                    checked={allowMultipleCompletions}
                    disabled={!isRepeatAllowed}
                    onChange={(e) =>
                      setAllowMultipleCompletions(e?.target.checked)
                    }
                  />
                  <p className="mt-1 ml-14 text-xs text-gray-600">
                    {!isRepeatAllowed ? (
                      // The server refuses this outright, so saying so here is
                      // kinder than letting an admin fill in the whole form
                      // and meet a validation error at the end.
                      <span className="text-orange-600">
                        {recurringChallengeTypes?.length ? (
                          <>
                            Repeated completions are switched off for this
                            challenge type on this environment.
                          </>
                        ) : (
                          <>
                            Repeated completions are switched off on this
                            environment. The app builds that can show more than
                            one completion have to be live first.
                          </>
                        )}
                      </span>
                    ) : isParcelChallenge ? (
                      <>
                        Not a schedule — nothing resets weekly or monthly. Every{" "}
                        {watch("minParcelCount") || "N"} parcels is another
                        completion, and anything left over counts towards the
                        next one.
                      </>
                    ) : (
                      <>
                        {watch("type") === "STREAKS"
                          ? null
                          : "Not a schedule - nothing resets weekly or monthly. "}
                        Every {repeatCycleLabel()} is another completion, and
                        anything left over counts towards the next one.
                      </>
                    )}
                  </p>
                </div>
                <p className="font-satoshi text-sm text-dark mb-6">
                  {isParcelChallenge
                    ? allowMultipleCompletions
                      ? "Each completion issues its own voucher, so stock is consumed as fast as customers earn it. A single large handoff can complete this several times at once."
                      : "Each customer can complete this challenge once. Reaching the target issues a single voucher from the reward partner above."
                    : repeatRewardText()}
                </p>
              </>
            ) : null}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Where it runs
              </label>
              <select
                value={targetType}
                onChange={(e) =>
                  setTargetType(e.target.value as "cities" | "locations")
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="cities">Cities (every store in them)</option>
                <option value="locations">Specific stores</option>
              </select>
              {targetType === "locations" ? (
                <p className="mt-1 text-xs text-gray-500">
                  Only customers whose Droppie location is one of these stores
                  see this challenge, and only drops and parcels at these
                  stores count. Cities are ignored.
                </p>
              ) : null}
            </div>
            {targetType === "cities" ? (
              <DynamicFormInput
                generateIds={false}
                addOnSelect
                label="Cities"
                inputs={cities}
                setInputs={setCities}
                options={citiesToSelect ?? []}
              />
            ) : (
              <DynamicFormInput
                generateIds={false}
                addOnSelect
                label="Stores"
                inputs={stores}
                setInputs={setStores}
                options={storesToSelect}
              />
            )}
            <ControlledInput
              name="startsAt"
              rules={{ required: false }}
              control={control}
              error={Boolean(errors?.startsAt)}
              label="Start Date (optional — leave empty to go live immediately)"
              type="date"
              placeholder=""
            />
            <ControlledInput
              name="endsAt"
              rules={{ required: false }}
              control={control}
              error={Boolean(errors?.endsAt)}
              label="Expire Date"
              type="date"
              placeholder=""
            />
            <div className="flex flex-row items-center justify-end">
              <SecondaryButton type="button" onClick={goBack} label="Cancel" />
              <PrimaryButton type="submit" className="ml-4" label="Save" />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default withProtectedRoute(AddPartnerChallengeScreen);

const types = [
  { _id: "FIRST_DROP", title: "First Drop" },
  { _id: "DROP_COUNT", title: "Drop Count" },
  { _id: "DROP_VALUE", title: "Drop Value" },
  { _id: "ITEM_TYPE", title: "Item Type" },
  { _id: "ITEM_TYPE_COUNT", title: "Item Type Count" },
  { _id: "ITEM_TYPE_DROP_COUNT", title: "Item Type Drop Count" },
  { _id: "ITEM_TYPE_VALUE", title: "Item Type Value" },
  { _id: "TOMRA_ITEM_TYPE", title: "Statie Item Type" },
  { _id: "TOMRA_ITEM_TYPE_COUNT", title: "Statie Item Type Count" },
  { _id: "TOMRA_ITEM_TYPE_DROP_COUNT", title: "Statie Item Type Drop Count" },
  { _id: "TOMRA_ITEM_TYPE_VALUE", title: "Statie Item Type Value" },
  { _id: "DONATION", title: "Donation" },
  { _id: "INVITE", title: "Invite" },
  { _id: "STREAKS", title: "Streaks" },
  { _id: "POSTAL_CODE", title: "Postal code" },
  { _id: "COMPLETE_PROFILE", title: "Complete Profile" },
  { _id: "PARCEL_COUNT", title: "Parcel Count" },
];

const parcelDirections = [
  { _id: "EITHER", title: "Pickups and drop-offs (added together)" },
  { _id: "PICKUP", title: "Pickup only" },
  { _id: "DROPOFF", title: "Drop-off only" },
];

const streakTypes = [
  { _id: "DAILY", title: "Daily" },
  { _id: "WEEKLY", title: "Weekly" },
  { _id: "MONTHLY", title: "Monthly" },
];

const streakPeriods: Record<string, string> = {
  DAILY: "days",
  WEEKLY: "weeks",
  MONTHLY: "months",
};

const formatEuros = (euros: number) =>
  new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR",
  }).format(euros);
